import React, { useState } from 'react';
import styled from 'styled-components';
import authService from '../../api/auth';
import { toast } from 'react-toastify';

const Form = styled.form`
  display: flex;
  flex-direction: column;
  transition: all 0.3s ease;
`;

const Input = styled.input`
  padding: 12px;
  margin-bottom: 15px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 5px;
  font-size: 16px;
  background-color: ${props => props.theme.inputBg};
  color: ${props => props.theme.text};
  
  &::placeholder {
    color: ${props => props.theme.textSecondary};
  }
`;

const Button = styled.button`
  padding: 12px;
  background-color: ${props => props.theme.primary};
  color: white;
  border: none;
  border-radius: 5px;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  margin-top: 10px;
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
  
  &:hover:not(:disabled) {
    background-color: ${props => props.theme.primaryHover};
  }
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.primary};
  font-size: 14px;
  margin-top: 15px;
  cursor: pointer;
  
  &:hover {
    text-decoration: underline;
  }
`;

const SuccessContainer = styled.div`
  text-align: center;
  color: ${props => props.theme.text};
  
  i {
    color: ${props => props.theme.success};
    font-size: 48px;
    margin-bottom: 15px;
  }
  
  p {
    margin-bottom: 20px;
  }
`;

const MessageText = styled.p`
  color: ${props => props.theme.text};
  margin-bottom: 15px;
`;

const ForgotPasswordForm = ({ onBackToLogin }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  
  // Generate a unique ID for this form instance
  const formId = React.useMemo(() => `forgot-password-form-${Math.random().toString(36).substr(2, 9)}`, []);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email.trim()) {
      toast.error('Por favor, ingresa tu correo electrónico.');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await authService.forgotPassword(email);
      
      if (response.success) {
        setSuccess(true);
        toast.success('Instrucciones enviadas a tu correo electrónico.');
      } else {
        toast.error(response.message || 'Error al solicitar recuperación.');
      }
    } catch (err) {
      if (err.response && err.response.status === 429) {
        // Manejar error de rate limiting
        toast.error(err.response.data.message || 'Demasiadas solicitudes. Por favor, intenta más tarde.');
      } else {
        toast.error('Error al conectar con el servidor. Intenta más tarde.');
      }
    } finally {
      setLoading(false);
    }
  };
  
  const handleChange = (e) => {
    setEmail(e.target.value.toLowerCase());
  };
  
  if (success) {
    return (
      <SuccessContainer>
        <i className="fas fa-check-circle"></i>
        <p>
          Hemos enviado un correo electrónico a <strong>{email}</strong> con instrucciones para recuperar tu contraseña.
        </p>
        <Button onClick={onBackToLogin}>Volver al inicio de sesión</Button>
      </SuccessContainer>
    );
  }
  
  return (
    <Form onSubmit={handleSubmit}>
      <MessageText>Ingresa tu correo electrónico y te enviaremos instrucciones para restablecer tu contraseña.</MessageText>
      
      <Input 
        type="email"
        placeholder="Correo electrónico"
        value={email}
        onChange={handleChange}
        disabled={loading}
        id={`${formId}-email`}
        name="forgot-password-email"
      />
      
      <Button type="submit" disabled={loading}>
        {loading ? 'Enviando...' : 'Recuperar contraseña'}
      </Button>
      
      <BackButton type="button" onClick={onBackToLogin}>
        Volver al inicio de sesión
      </BackButton>
    </Form>
  );
};

export default ForgotPasswordForm;